import { ApiError } from '@/api/ApiError'
import type { ApiErrorResource } from '@/components/ApiErrorState/ApiErrorState'
import {
  AUDIT_LOG_STEP_SIZE,
  type AuditLogQuery,
  toBackofficeAuditParams,
  toV3AuditParams,
} from '@/features/AuditLogs/lib/auditSourceParams'
import {
  type AuditSourceSnapshot,
  lastPageAddsNewEntry,
  mergeAuditSources,
} from '@/features/AuditLogs/lib/mergeAuditSources'
import { fetchListAuditEntries } from '@/generated/http-clients/backofficeApiComponents'
import { useBackofficeApiContext } from '@/generated/http-clients/backofficeApiContext'
import type { AuditLogPage } from '@/generated/http-clients/backofficeApiSchemas'
import { fetchGetAuditLogs } from '@/generated/http-clients/v3ApiComponents'
import { useV3ApiContext } from '@/generated/http-clients/v3ApiContext'
import type {
  AuditLogs,
  AuditLogsResponse,
} from '@/generated/http-clients/v3ApiSchemas'
import {
  type InfiniteData,
  type QueryKey,
  useInfiniteQuery,
} from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useState } from 'react'

export interface MergedAuditLogs {
  entries: AuditLogs[]
  isLoading: boolean
  isError: boolean
  isEmpty: boolean
  isIncomplete: boolean
  isFetchingMore: boolean
  hasMore: boolean
  errorResource: ApiErrorResource
  loadMore: () => void
  refresh: () => void
}

function retryUnlessClientError(failureCount: number, error: unknown) {
  return (
    !(error instanceof ApiError && error.status >= 400 && error.status < 500) &&
    failureCount < 3
  )
}

export function useMergedAuditLogs(query: AuditLogQuery): MergedAuditLogs {
  const v3Params = useMemo(() => toV3AuditParams(query), [query])
  const backofficeParams = useMemo(
    () => toBackofficeAuditParams(query),
    [query],
  )

  const [generation, setGeneration] = useState(0)
  const refresh = useCallback(() => setGeneration((g) => g + 1), [])

  const { fetcherOptions: v3FetcherOptions } = useV3ApiContext()
  const { fetcherOptions: backofficeFetcherOptions } = useBackofficeApiContext()

  const v3Query = useInfiniteQuery<
    AuditLogsResponse,
    Error,
    InfiniteData<AuditLogsResponse, number>,
    QueryKey,
    number
  >({
    queryKey: ['api', 'v3', 'audit', 'logs', v3Params, 'merged', generation],
    queryFn: ({ pageParam, signal }) =>
      fetchGetAuditLogs(
        {
          ...v3FetcherOptions,
          queryParams: { ...v3Params, page: pageParam },
        },
        signal,
      ),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _pages, lastPageParam) =>
      lastPage.has_next_page ? lastPageParam + 1 : undefined,
    enabled: v3Params !== null,
    refetchOnWindowFocus: false,
    retry: retryUnlessClientError,
  })

  const backofficeQuery = useInfiniteQuery<
    AuditLogPage,
    Error,
    InfiniteData<AuditLogPage, string | null>,
    QueryKey,
    string | null
  >({
    queryKey: ['api', 'v1', 'audit', backofficeParams, 'merged', generation],
    queryFn: ({ pageParam, signal }) =>
      fetchListAuditEntries(
        {
          ...backofficeFetcherOptions,
          queryParams:
            pageParam === null
              ? backofficeParams
              : { ...backofficeParams, cursor: pageParam },
        },
        signal,
      ),
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    refetchOnWindowFocus: false,
    retry: retryUnlessClientError,
  })

  const key = JSON.stringify([v3Params, backofficeParams, generation])
  const [stepState, setStepState] = useState({ key, steps: 1 })
  const steps = stepState.key === key ? stepState.steps : 1
  const loadMore = useCallback(
    () =>
      setStepState((prev) => ({
        key,
        steps: (prev.key === key ? prev.steps : 1) + 1,
      })),
    [key],
  )
  const limit = steps * AUDIT_LOG_STEP_SIZE

  const v3Snapshot = useMemo<AuditSourceSnapshot>(() => {
    if (v3Params === null) {
      return { entries: [], canLoadMore: false, failed: false }
    }
    const entries =
      v3Query.data?.pages.flatMap((page) => page.audit_logs ?? []) ?? []
    if (v3Query.isError) {
      return { entries, canLoadMore: false, failed: true }
    }
    return {
      entries,
      canLoadMore:
        v3Query.data === undefined ||
        (v3Query.hasNextPage &&
          lastPageAddsNewEntry(
            v3Query.data.pages.map((page) => page.audit_logs ?? []),
          )),
      failed: false,
    }
  }, [v3Params, v3Query.data, v3Query.isError, v3Query.hasNextPage])

  const backofficeSnapshot = useMemo<AuditSourceSnapshot>(() => {
    const entries =
      backofficeQuery.data?.pages.flatMap((page) => page.entries) ?? []
    if (backofficeQuery.isError) {
      return { entries, canLoadMore: false, failed: true }
    }
    return {
      entries,
      canLoadMore:
        backofficeQuery.data === undefined ||
        (backofficeQuery.hasNextPage &&
          lastPageAddsNewEntry(
            backofficeQuery.data.pages.map((page) => page.entries),
          )),
      failed: false,
    }
  }, [
    backofficeQuery.data,
    backofficeQuery.isError,
    backofficeQuery.hasNextPage,
  ])

  const merged = useMemo(
    () =>
      mergeAuditSources(
        { v3: v3Snapshot, backoffice: backofficeSnapshot },
        limit,
      ),
    [v3Snapshot, backofficeSnapshot, limit],
  )
  const waitingKey = merged.waitingFor.join(',')

  useEffect(() => {
    const waiting = waitingKey.split(',')
    if (
      waiting.includes('v3') &&
      v3Query.data !== undefined &&
      !v3Query.isFetching
    ) {
      v3Query.fetchNextPage()
    }
    if (
      waiting.includes('backoffice') &&
      backofficeQuery.data !== undefined &&
      !backofficeQuery.isFetching
    ) {
      backofficeQuery.fetchNextPage()
    }
  }, [
    waitingKey,
    v3Query.data,
    v3Query.isFetching,
    v3Query.fetchNextPage,
    backofficeQuery.data,
    backofficeQuery.isFetching,
    backofficeQuery.fetchNextPage,
  ])

  const queriedSnapshots =
    v3Params === null ? [backofficeSnapshot] : [v3Snapshot, backofficeSnapshot]
  const hasNoEntries = merged.entries.length === 0
  const isWaiting = merged.waitingFor.length > 0
  const isError = queriedSnapshots.every((s) => s.failed) && hasNoEntries
  const isIncomplete = queriedSnapshots.some((s) => s.failed) && !isError
  const failedQuery = v3Snapshot.failed ? v3Query : backofficeQuery
  const errorResource: ApiErrorResource = {
    error: failedQuery.error,
    isLoading: failedQuery.isLoading,
    refetch: refresh,
  }
  const isLoading = !isError && hasNoEntries && isWaiting

  return {
    entries: merged.entries,
    isLoading,
    isError,
    isEmpty: !isLoading && !isError && hasNoEntries,
    isIncomplete,
    isFetchingMore: !hasNoEntries && isWaiting,
    hasMore: merged.hasMore,
    errorResource,
    loadMore,
    refresh,
  }
}
